import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StudioView } from '../../types'
import { GlassPanel, monoLabelSx, ResetGlyph } from '../shared'

export interface StudioViewSwitcherProps {
  view: StudioView
  zoomPercent: number
  onViewChange: (view: StudioView) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  /** Hides Walk — a loaded Classic room has no collision, so first-person movement isn't offered while one is active. */
  canWalk?: boolean
}

const Root = styled(Box)({ position: 'absolute', left: 12, bottom: 16, zIndex: 40, display: 'flex', alignItems: 'center', gap: 8 })

const Vseg = styled(GlassPanel)({ display: 'flex', padding: 4, gap: 2, borderRadius: 12 })

const VBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  height: 30,
  padding: '0 13px',
  borderRadius: 8,
  fontSize: 11.5,
  fontWeight: 620,
  cursor: 'pointer',
  color: $on ? 'var(--ds-night-ink)' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(244,243,246,0.1)' : 'transparent',
  transition: 'all 140ms ease',
}))

const EyeHeight = styled(GlassPanel)({
  ...monoLabelSx,
  height: 38,
  padding: '0 13px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: 12,
  fontSize: 9.5,
  letterSpacing: '0.06em',
  color: 'var(--ds-night-ink-muted)',
})

/**
 * Zoom readout, now interactive. `−`/`+` used to be hover-revealed (zero
 * opacity + `pointerEvents: none` until the pill was hovered) so this looked
 * exactly like the old plain `{zoomPercent}%` pill at rest — but that meant
 * the controls were invisible to anyone not actively hovering (a static
 * screenshot, a DOM inspector snapshot, or just a user who never happened to
 * mouse over this exact corner), repeatedly read as "the buttons don't
 * exist." Always visible now, dimmed at rest and brightening per-button on
 * hover — same restraint, without the total invisibility.
 */
const ZoomPill = styled(GlassPanel)({
  height: 38,
  padding: '0 6px',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  borderRadius: 12,
})

const ZoomStepBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 22,
  height: 22,
  borderRadius: 6,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  // `ink-subtle` (36% white) reads fine for multi-letter labels elsewhere in
  // this file, but a single thin glyph like `−` has almost no stroke area to
  // carry that opacity — it was effectively invisible even at rest.
  // `ink-muted` (60%) keeps the "quieter than the %, brighter on hover"
  // hierarchy but is actually legible at this size/weight.
  color: 'var(--ds-night-ink-muted)',
  transition: 'background 140ms ease, color 140ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' },
})

const ZoomNum = styled('span')({
  ...monoLabelSx,
  fontSize: 9.5,
  letterSpacing: '0.06em',
  color: 'var(--ds-night-ink-muted)',
  padding: '0 5px',
  flexShrink: 0,
})

/** Only rendered once zoom has actually moved off 100% — not part of the hover reveal, so it's discoverable without hovering first. */
const ZoomResetBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 20,
  height: 20,
  borderRadius: 6,
  flexShrink: 0,
  marginLeft: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffcf7d',
  cursor: 'pointer',
  transition: 'background 140ms ease',
  '&:hover': { background: 'rgba(255,207,125,0.14)' },
  '& svg': { display: 'block', width: 12, height: 12 },
})

const VIEWS: { value: StudioView; label: string }[] = [
  { value: 'orbit', label: 'Orbit' },
  { value: 'plan', label: 'Plan' },
  { value: 'walk', label: 'Walk' },
]

/** Bottom-left orbit/plan/walk switcher + zoom readout (Walk shows eye height instead — no zoom control while walking). */
export function StudioViewSwitcher({ view, zoomPercent, onViewChange, onZoomIn, onZoomOut, onResetZoom, canWalk = true }: StudioViewSwitcherProps) {
  const views = canWalk ? VIEWS : VIEWS.filter((v) => v.value !== 'walk')
  return (
    <Root>
      <Vseg>
        {views.map((v) => (
          <VBtn key={v.value} $on={view === v.value} onClick={() => onViewChange(v.value)}>
            {v.label}
          </VBtn>
        ))}
      </Vseg>
      {view === 'walk' ? (
        <EyeHeight>EYE · 1.62 M</EyeHeight>
      ) : (
        <ZoomPill>
          <ZoomStepBtn type='button' onClick={onZoomOut} aria-label='Zoom out'>
            −
          </ZoomStepBtn>
          <ZoomNum>{zoomPercent}%</ZoomNum>
          <ZoomStepBtn type='button' onClick={onZoomIn} aria-label='Zoom in'>
            +
          </ZoomStepBtn>
          {zoomPercent !== 100 && (
            <ZoomResetBtn type='button' onClick={onResetZoom} aria-label='Reset zoom'>
              <ResetGlyph size={12} />
            </ZoomResetBtn>
          )}
        </ZoomPill>
      )}
    </Root>
  )
}
